import React, { useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Users, Plus, Edit2, Trash2, RefreshCw } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useGroupSessions } from '@/hooks/useGroupSessions';
import { GroupSession, GroupSessionScope } from '@/types/groupSession';
import { Participant } from '@/types/category';
import ParticipantsEditor from './ParticipantsEditor';

interface GroupSessionSelectorProps {
  value: GroupSessionScope;
  onChange: (scope: GroupSessionScope) => void;
}

const emptyParticipants: Participant[] = [];

const GroupSessionSelector: React.FC<GroupSessionSelectorProps> = ({ value, onChange }) => {
  const { groupSessions, createGroupSession, updateGroupSession, deleteGroupSession, loading, refreshGroupSessions } = useGroupSessions();
  const { toast } = useToast();
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState<GroupSession | null>(null);
  const [newGroupName, setNewGroupName] = useState('');
  const [newGroupType, setNewGroupType] = useState<'family' | 'organization' | 'friends' | 'team' | 'yoga'>('family');
  const [newGroupDescription, setNewGroupDescription] = useState('');
  const [newGroupParticipants, setNewGroupParticipants] = useState<Participant[]>(emptyParticipants);
  const [yogaDirectives, setYogaDirectives] = useState('');
  const [openingPositions, setOpeningPositions] = useState('');
  const [endingPositions, setEndingPositions] = useState('');
  const [sessionTiming, setSessionTiming] = useState<'pre-yoga' | 'post-yoga' | 'standalone'>('standalone');
  const [organizationName, setOrganizationName] = useState('');
  const [organizationBiography, setOrganizationBiography] = useState('');
  const [department, setDepartment] = useState('');


  const handleGroupSelection = (groupId: string) => {
    const selectedGroup = groupSessions.find(gs => gs.id === groupId);
    onChange({
      sessionScope: 'group',
      groupSessionId: groupId,
      groupSession: selectedGroup
    });
  };

  const handleCreateGroup = async () => {
    // Validate form data
    if (!newGroupName.trim()) {
      toast({
        title: 'Validation Error',
        description: 'Group name is required',
        variant: 'destructive',
      });
      return;
    }

    if (newGroupType === 'organization' && !organizationName.trim()) {
      toast({
        title: 'Validation Error',
        description: 'Organization name is required for organizational groups',
        variant: 'destructive',
      });
      return;
    }

    console.log('🚀 Creating group with data:', {
      group_name: newGroupName,
      group_type: newGroupType,
      description: newGroupDescription,
      participants: newGroupParticipants
    });

    const newGroup = await createGroupSession({
      group_name: newGroupName,
      group_type: newGroupType,
      description: newGroupDescription,
      participants: newGroupParticipants,
      yoga_directives: newGroupType === 'yoga' ? yogaDirectives : undefined,
      opening_positions: newGroupType === 'yoga' ? openingPositions : undefined,
      ending_positions: newGroupType === 'yoga' ? endingPositions : undefined,
      session_timing: newGroupType === 'yoga' ? sessionTiming : undefined,
      organization_name: newGroupType === 'organization' ? organizationName : undefined,
      organization_biography: newGroupType === 'organization' ? organizationBiography : undefined,
      department: newGroupType === 'organization' ? department : undefined,
    });

    if (newGroup) {
      console.log('✅ Group created successfully, selecting it:', newGroup);
      
      onChange({
        sessionScope: 'group',
        groupSessionId: newGroup.id,
        groupSession: newGroup
      });
      
      // Reset form
      setNewGroupName('');
      setNewGroupType('family');
      setNewGroupDescription('');
      setNewGroupParticipants(emptyParticipants);
      setYogaDirectives('');
      setOpeningPositions('');
      setEndingPositions('');
      setSessionTiming('standalone');
      setOrganizationName('');
      setOrganizationBiography('');
      setDepartment('');
      setIsCreateDialogOpen(false);
    } else {
      console.log('❌ Group creation failed');
    }
  };

  const handleEditGroup = (group: GroupSession) => {
    setEditingGroup(group);
    setNewGroupName(group.group_name);
    setNewGroupType(group.group_type);
    setNewGroupDescription(group.description || '');
    setNewGroupParticipants(group.participants);
    setYogaDirectives(group.yoga_directives || '');
    setOpeningPositions(group.opening_positions || '');
    setEndingPositions(group.ending_positions || '');
    setSessionTiming(group.session_timing || 'standalone');
    setOrganizationName(group.organization_name || '');
    setOrganizationBiography(group.organization_biography || '');
    setDepartment(group.department || '');
    setIsEditDialogOpen(true);
  };

  const handleUpdateGroup = async () => {
    if (!editingGroup || !newGroupName.trim()) return;

    const updatedGroup = await updateGroupSession(editingGroup.id, {
      group_name: newGroupName,
      group_type: newGroupType,
      description: newGroupDescription,
      participants: newGroupParticipants,
      yoga_directives: newGroupType === 'yoga' ? yogaDirectives : undefined,
      opening_positions: newGroupType === 'yoga' ? openingPositions : undefined,
      ending_positions: newGroupType === 'yoga' ? endingPositions : undefined,
      session_timing: newGroupType === 'yoga' ? sessionTiming : undefined,
      organization_name: newGroupType === 'organization' ? organizationName : undefined,
      organization_biography: newGroupType === 'organization' ? organizationBiography : undefined,
      department: newGroupType === 'organization' ? department : undefined,
    });

    if (updatedGroup) {
      onChange({
        sessionScope: 'group',
        groupSessionId: updatedGroup.id,
        groupSession: updatedGroup
      });
      
      // Reset form
      setNewGroupName('');
      setNewGroupType('family');
      setNewGroupDescription('');
      setNewGroupParticipants(emptyParticipants);
      setYogaDirectives('');
      setOpeningPositions('');
      setEndingPositions('');
      setSessionTiming('standalone');
      setOrganizationName('');
      setOrganizationBiography('');
      setDepartment('');
      setEditingGroup(null);
      setIsEditDialogOpen(false);
    }
  };

  const handleDeleteGroup = async (groupId: string) => {
    if (window.confirm('Are you sure you want to delete this group? This action cannot be undone.')) {
      const success = await deleteGroupSession(groupId);
      if (success && value.groupSessionId === groupId) {
        // Clear the group selection but stay in group mode
        onChange({ sessionScope: 'group' });
      }
    }
  };

  const getGroupTypeIcon = (type: string) => {
    switch (type) {
      case 'family': return '👨‍👩‍👧‍👦';
      case 'organization': return '🏢';
      case 'friends': return '👥';
      case 'team': return '⚡';
      case 'yoga': return '🧘‍♀️';
      default: return '👥';
    }
  };

  return (
    <Card className="p-4 space-y-4">
      <div className="flex items-center gap-2">
        <Users className="h-5 w-5 text-primary" />
        <Label className="text-base font-medium">Select or Create Group</Label>
      </div>

      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <Label className="text-sm">Select Group</Label>
                <div className="flex gap-2">
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={refreshGroupSessions}
                    disabled={loading}
                    className="h-8 px-2"
                  >
                    <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                  </Button>
                  <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
                    <DialogTrigger asChild>
                      <Button variant="outline" size="sm">
                        <Plus className="h-4 w-4 mr-1" />
                        New Group
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                      <DialogHeader>
                        <DialogTitle>Create New Group</DialogTitle>
                      </DialogHeader>
                      <div className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="space-y-1">
                            <Label>Group Name</Label>
                            <Input
                              value={newGroupName}
                              onChange={(e) => setNewGroupName(e.target.value)}
                              placeholder="e.g., Our Family, Design Team"
                            />
                          </div>
                          <div className="space-y-1">
                            <Label>Group Type</Label>
                            <Select value={newGroupType} onValueChange={(value: any) => setNewGroupType(value)}>
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="family">👨‍👩‍👧‍👦 Family</SelectItem>
                                <SelectItem value="organization">🏢 Organization</SelectItem>
                                <SelectItem value="friends">👥 Friends</SelectItem>
                                <SelectItem value="team">⚡ Team</SelectItem>
                                <SelectItem value="yoga">🧘‍♀️ Yoga Class</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                        <div className="space-y-1">
                          <Label>Description (optional)</Label>
                          <Textarea
                            value={newGroupDescription}
                            onChange={(e) => setNewGroupDescription(e.target.value)}
                            placeholder="Brief description of your group..."
                            rows={2}
                          />
                         </div>

                         {newGroupType === 'organization' && (
                           <>
                             <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                               <div className="space-y-1">
                                 <Label>Organization Name *</Label>
                                 <Input
                                   value={organizationName}
                                   onChange={(e) => setOrganizationName(e.target.value)}
                                   placeholder="e.g., TechCorp Inc"
                                 />
                               </div>
                               <div className="space-y-1">
                                 <Label>Department/Team</Label>
                                 <Input
                                   value={department}
                                   onChange={(e) => setDepartment(e.target.value)}
                                   placeholder="e.g., Engineering, Marketing"
                                 />
                               </div>
                             </div>
                             <div className="space-y-1">
                               <Label>Organization Context</Label>
                               <Textarea
                                 value={organizationBiography}
                                 onChange={(e) => setOrganizationBiography(e.target.value)}
                                 placeholder="Describe your organization's culture, values, or specific context..."
                                 rows={3}
                               />
                             </div>
                           </>
                         )}

                          {newGroupType === 'yoga' && (
                           <>
                             <div className="space-y-1">
                               <Label>Session Timing</Label>
                               <Select value={sessionTiming} onValueChange={(value: any) => setSessionTiming(value)}>
                                 <SelectTrigger>
                                   <SelectValue />
                                 </SelectTrigger>
                                 <SelectContent>
                                   <SelectItem value="standalone">🧘‍♀️ Complete Yoga Session</SelectItem>
                                   <SelectItem value="pre-yoga">🔥 Pre-Yoga Preparation</SelectItem>
                                   <SelectItem value="post-yoga">🌙 Post-Yoga Integration</SelectItem>
                                 </SelectContent>
                               </Select>
                               <p className="text-xs text-muted-foreground">
                                 {sessionTiming === 'pre-yoga' && 'Energizing meditation to prepare for yoga practice'}
                                 {sessionTiming === 'post-yoga' && 'Calming meditation to integrate after yoga practice'}
                                 {sessionTiming === 'standalone' && 'Complete standalone yoga meditation session'}
                               </p>
                             </div>
                             <div className="space-y-1">
                               <Label>Yoga Class Directives</Label>
                               <Textarea
                                 value={yogaDirectives}
                                 onChange={(e) => setYogaDirectives(e.target.value)}
                                 placeholder="Specific instructions for this yoga class (e.g., focus areas, modifications, breathing techniques)..."
                                 rows={3}
                               />
                             </div>
                             <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                               <div className="space-y-1">
                                 <Label>Default Opening Positions</Label>
                                 <Input
                                   value={openingPositions}
                                   onChange={(e) => setOpeningPositions(e.target.value)}
                                   placeholder="e.g., Child's Pose, Mountain Pose"
                                 />
                               </div>
                               <div className="space-y-1">
                                 <Label>Default Ending Positions</Label>
                                 <Input
                                   value={endingPositions}
                                   onChange={(e) => setEndingPositions(e.target.value)}
                                   placeholder="e.g., Savasana, Seated Meditation"
                                 />
                               </div>
                             </div>
                           </>
                         )}
                        <div>
                          <ParticipantsEditor
                            participants={newGroupParticipants}
                            onChange={setNewGroupParticipants}
                          />
                        </div>
                        <div className="flex justify-end gap-2">
                          <Button variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
                            Cancel
                          </Button>
                          <Button 
                            onClick={() => {
                              console.log('🔘 Create Group button clicked');
                              console.log('🔘 Button state - loading:', loading);
                              console.log('🔘 Button state - newGroupName:', newGroupName);
                              console.log('🔘 Button state - newGroupParticipants:', newGroupParticipants);
                              console.log('🔘 Button disabled?', loading || !newGroupName.trim());
                              handleCreateGroup();
                            }}
                            disabled={loading || !newGroupName.trim()}
                          >
                            {loading ? 'Creating...' : 'Create Group'}
                          </Button>
                        </div>
                      </div>
                    </DialogContent>
                  </Dialog>
                </div>
              </div>

              {groupSessions.length > 0 ? (
                <Select value={value.groupSessionId || ''} onValueChange={handleGroupSelection}>
                  <SelectTrigger>
                    <SelectValue placeholder="Choose a group..." />
                  </SelectTrigger>
                  <SelectContent>
                    {groupSessions.map((group) => (
                      <SelectItem key={group.id} value={group.id}>
                        <div className="flex items-center gap-2">
                          <span>{getGroupTypeIcon(group.group_type)}</span>
                          <span>{group.group_name}</span>
                          <Badge variant="secondary" className="ml-auto">
                            {group.participants.length} members
                          </Badge>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <div className="text-center py-4 text-muted-foreground">
                  <Users className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No groups created yet</p>
                  <p className="text-xs">Create your first group to get started</p>
                </div>
              )}

              {value.groupSession && (
                <Card className="p-3 bg-muted/50">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className="text-lg">{getGroupTypeIcon(value.groupSession.group_type)}</span>
                      <span className="font-medium">{value.groupSession.group_name}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline">
                        {value.groupSession.participants.length} members
                      </Badge>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEditGroup(value.groupSession!)}
                        className="h-8 w-8 p-0"
                      >
                        <Edit2 className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteGroup(value.groupSession!.id)}
                        className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  {value.groupSession.description && (
                    <p className="text-sm text-muted-foreground mb-2">{value.groupSession.description}</p>
                  )}
                  <div className="flex flex-wrap gap-1">
                    {value.groupSession.participants.map((participant, idx) => (
                      <Badge key={idx} variant="secondary" className="text-xs">
                        {participant.preferredName || participant.firstName}
                      </Badge>
                    ))}
                  </div>
                </Card>
              )}

              {/* Edit Group Dialog */}
              <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>Edit Group</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-1">
                        <Label>Group Name</Label>
                        <Input
                          value={newGroupName}
                          onChange={(e) => setNewGroupName(e.target.value)}
                          placeholder="e.g., Our Family, Design Team"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label>Group Type</Label>
                        <Select value={newGroupType} onValueChange={(value: any) => setNewGroupType(value)}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="family">👨‍👩‍👧‍👦 Family</SelectItem>
                              <SelectItem value="organization">🏢 Organization</SelectItem>
                              <SelectItem value="friends">👥 Friends</SelectItem>
                              <SelectItem value="team">⚡ Team</SelectItem>
                              <SelectItem value="yoga">🧘‍♀️ Yoga Class</SelectItem>
                            </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="space-y-1">
                      <Label>Description (optional)</Label>
                      <Textarea
                        value={newGroupDescription}
                        onChange={(e) => setNewGroupDescription(e.target.value)}
                        placeholder="Brief description of your group..."
                        rows={2}
                      />
                    </div>

                     {newGroupType === 'yoga' && (
                       <>
                         <div className="space-y-1">
                           <Label>Session Timing</Label>
                           <Select value={sessionTiming} onValueChange={(value: any) => setSessionTiming(value)}>
                             <SelectTrigger>
                               <SelectValue />
                             </SelectTrigger>
                             <SelectContent>
                               <SelectItem value="standalone">🧘‍♀️ Complete Yoga Session</SelectItem>
                               <SelectItem value="pre-yoga">🔥 Pre-Yoga Preparation</SelectItem>
                               <SelectItem value="post-yoga">🌙 Post-Yoga Integration</SelectItem>
                             </SelectContent>
                           </Select>
                           <p className="text-xs text-muted-foreground">
                             {sessionTiming === 'pre-yoga' && 'Energizing meditation to prepare for yoga practice'}
                             {sessionTiming === 'post-yoga' && 'Calming meditation to integrate after yoga practice'}
                             {sessionTiming === 'standalone' && 'Complete standalone yoga meditation session'}
                           </p>
                         </div>
                         <div className="space-y-1">
                           <Label>Yoga Class Directives</Label>
                           <Textarea
                             value={yogaDirectives}
                             onChange={(e) => setYogaDirectives(e.target.value)}
                             placeholder="Specific instructions for this yoga class..."
                             rows={3}
                           />
                         </div>
                         <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                           <div className="space-y-1">
                             <Label>Default Opening Positions</Label>
                             <Input
                               value={openingPositions}
                               onChange={(e) => setOpeningPositions(e.target.value)}
                               placeholder="e.g., Child's Pose, Mountain Pose"
                             />
                           </div>
                           <div className="space-y-1">
                             <Label>Default Ending Positions</Label>
                             <Input
                               value={endingPositions}
                               onChange={(e) => setEndingPositions(e.target.value)}
                               placeholder="e.g., Savasana, Seated Meditation"
                             />
                           </div>
                         </div>
                       </>
                     )}
                    <div>
                      <ParticipantsEditor
                        participants={newGroupParticipants}
                        onChange={setNewGroupParticipants}
                      />
                    </div>
                    <div className="flex justify-end gap-2">
                      <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>
                        Cancel
                      </Button>
                      <Button onClick={handleUpdateGroup} disabled={loading || !newGroupName.trim()}>
                        Update Group
                      </Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
            </div>
    </Card>
  );
};

export default GroupSessionSelector;
